import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { kc, kindName } from '../../copyKinds'
import { answerKind, dismissAsk, useKinds } from '../../lib/kinds'
import { buttonClass } from '../../ui/styles'
import { FADE, spring } from '../../ui/motion'

/** Level-2 kinds (10/18): one tap says "more of this kind" (a still_into yes on sub:<slug>). Only kinds that show up on
 *  enough of the listings near them are offered (lib/kinds.ts kindsIn). A tapped chip stays on: a close is not a no. */
export function KindChips({ kinds, source = 'kind_chip', className = '' }: { kinds: string[]; source?: 'kind_chip' | 'taste_kinds'; className?: string }) {
  const { answers } = useKinds()
  const [busy, setBusy] = useState<string | null>(null)
  if (!kinds.length) return null
  const tap = (k: string) => {
    if (answers[k] === 'yes' || busy) return
    setBusy(k)
    void answerKind(k, 'yes', source).catch(() => {}).finally(() => setBusy(null))
  }
  return (
    <div data-review-id="kind-chips" className={className}>
      <p className="t-label text-ink-2">{kc('kinds.chips_label')}</p>
      <ul className="mt-2 flex flex-wrap gap-2" aria-label={kc('kinds.chips_aria')}>
        {kinds.map((k) => {
          const on = answers[k] === 'yes'
          return (
            <li key={k}>
              <button type="button" onClick={() => tap(k)} aria-pressed={on} aria-busy={busy === k || undefined}
                aria-label={on ? kc('kinds.chip_on', { kind: kindName(k) }) : kindName(k)}
                className={`press t-control inline-flex min-h-11 items-center rounded-full px-4 ring-1 ring-inset ${on ? 'bg-accent text-on-accent ring-accent' : 'bg-surface text-ink ring-line hover:bg-sunk'}`}>
                {on ? '✓ ' : ''}{kindName(k)}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** "More <kind>?" after the 2nd save of one kind (lib/kinds.ts noteSaveKinds). Yes / Not really answer the taste
 *  question; the close button only stops the asking on this phone. */
export function KindAskBar() {
  const { asking } = useKinds()
  const reduce = useReducedMotion() ?? false
  const [busy, setBusy] = useState(false)
  // B-217: on a phone the detail sheet (z-50, drawn later) and its sticky Save bar covered this bar, so "Less, please"
  // landed on Save (an unsave, no kind signal). While a sheet footer is open, sit just above it and over the sheet.
  const [lift, setLift] = useState<number | null>(null)
  useEffect(() => {
    if (!asking) return
    const measure = () => {
      const f = [...document.querySelectorAll<HTMLElement>('[data-sheet-footer]')].find((el) => el.offsetParent !== null)
      setLift(f ? Math.round(window.innerHeight - f.getBoundingClientRect().top) : null)
    }
    measure()
    const t = window.setInterval(measure, 250)
    window.addEventListener('resize', measure)
    return () => { window.clearInterval(t); window.removeEventListener('resize', measure) }
  }, [asking])
  const say = (a: 'yes' | 'no') => {
    if (!asking || busy) return
    setBusy(true)
    void answerKind(asking, a, 'kind_ask').catch(() => dismissAsk()).finally(() => setBusy(false))
  }
  return (
    <AnimatePresence>
      {asking && (
        <motion.div key={asking} data-review-id="kind-ask" role="region" aria-label={kc('kinds.ask', { kind: kindName(asking, true) })}
          initial={reduce ? { opacity: 0 } : { y: 120 }} animate={{ y: 0, opacity: 1 }} exit={reduce ? { opacity: 0 } : { y: 160 }}
          transition={reduce ? FADE.enter : spring('layout')}
          style={lift != null ? { bottom: lift } : undefined}
          className={`fixed inset-x-0 ${lift != null ? 'z-[60]' : 'bottom-[calc(56px+env(safe-area-inset-bottom))] z-50'} border-t border-line bg-surface px-5 pt-3 pb-4 shadow-floating lg:inset-x-auto lg:right-8 lg:bottom-8 lg:w-[440px] lg:rounded-[20px] lg:ring-1 lg:ring-line`}>
          <div className="mx-auto flex w-full max-w-md flex-wrap items-center gap-2 md:max-w-[640px] lg:max-w-none">
            <p aria-live="polite" className="t-control min-w-0 flex-1 text-ink">{kc('kinds.ask', { kind: kindName(asking, true) })}</p>
            <button type="button" onClick={() => say('yes')} disabled={busy} className={buttonClass('primary', 'md', 'shrink-0 px-4')}>{kc('kinds.yes')}</button>
            <button type="button" onClick={() => say('no')} disabled={busy} className={buttonClass('secondary', 'md', 'shrink-0 px-4')}>{kc('kinds.no')}</button>
            <button type="button" aria-label={kc('kinds.close')} onClick={dismissAsk} className="press -mr-2 inline-flex size-11 shrink-0 items-center justify-center rounded-[12px] text-ink-2 hover:bg-sunk">×</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
